import React, { useCallback, useMemo, useRef } from 'react';

import {
  ColumnsIcon, LinkExternalIcon, PlayIcon, XCircleIcon
} from '@primer/octicons-react';

import parseISO from 'date-fns/parseISO';

import COLORS from '../../../constants/colors.constants';

import useWorkspaceContext from '../../workspaces/WorkspaceContext/WorkspaceContext';

import {
  formatSequencedBarChartDatasets,
  getBarChartComparisonDatasets,
} from '../../charts/helpers/dataset.helpers';

import {
  deriveAssayChartDatasetsByStatus,
  mapAssayBarData,
  mapAssayPointData,
} from '../../../utils/assay_utils';
import { formatChartPointDate } from '../../../utils/date_utils';

import changeAssayStatus from '../../../utils/api_functions/changeAssayStatus';

import { assayScoreFormatter, scientificNotationFormatter } from '../../../utils/formatters';

import { ChartProvider } from '../../charts/ChartContext';

import { formatTooltipDate, useRefDimensions } from '../../charts/d3-charts/d3-helpers';

import ChartWrapper from '../../charts/d3-charts/ChartWrapper';
import Label from '../../common/Labels/Label';
import MultiBarChart from '../../charts/d3-charts/bar/D3MultiBarChart';
import PointChart from '../../charts/d3-charts/points/D3PointChart';

import { ASSAY_RUN_STATUS } from '../../../constants/assay.constants';

const renderResultsTooltip = tooltipPoint => {
  const fixedNumber = tooltipPoint ? Number(tooltipPoint.y).toFixed(3) : null;
  const scientificNotation = tooltipPoint ? Number(tooltipPoint.y).toExponential(3) : null;

  const displayNumber = fixedNumber === '0.000' ? scientificNotation : fixedNumber;
  return (
    <div className="flex flex-col justify-center items-center">
      {
        tooltipPoint?.hoverData && (
          <Label color="white">
            {tooltipPoint.hoverData.label},{tooltipPoint.hoverData.labelVal}
          </Label>
        )
      }
      {
        tooltipPoint && (
          <div className="flex justify-center items-center">
            <div
              className="mr-1 w-4 h-4"
              style={{ backgroundColor: tooltipPoint.hoverData.color }}
            />
            <Label color="white">
              { tooltipPoint.x }: { displayNumber }
            </Label>
          </div>
        )
      }
    </div>
  )
}

const renderHistoryTooltip = tooltipPoint => {
  const fixedNumber = tooltipPoint ? Number(tooltipPoint.y).toFixed(3) : null;
  const scientificNotation = tooltipPoint ? Number(tooltipPoint.y).toExponential(3) : null;

  const displayNumber = fixedNumber === '0.000' ? scientificNotation : fixedNumber;
  return (
    <div className="flex flex-col justify-center items-center">
      {
        tooltipPoint && (
          <Label color="white">
            { formatTooltipDate(tooltipPoint.x) },
          </Label>
        )
      }
      {
        tooltipPoint && (
          <div className="flex justify-center items-center">
            {
              <>
                <div
                  className="mr-1 w-4 h-4"
                  style={{
                    backgroundColor: tooltipPoint?.status === ASSAY_RUN_STATUS.ALERT ? COLORS.error : COLORS.success,
                  }}
                />
                <Label color="white">
                  { tooltipPoint.status }: { displayNumber }
                </Label>
              </>
            }
          </div>
        )
      }
    </div>
  )
}

export const AssayResultBarChart = ({
  assayName,
  assayRunResult,
  assayDetails,
  showDetailsFor,
  onClose,
  onStatusToggle,
  showIconMenu = false,
  isLoading = false,
  hideNormalDropdownOptions = false,
  hideCardTitle = false,
  message = null,
}) => {
  const assayStatus = assayDetails?.active;

  const normalModalOptions = [
    {
      id: 'changeAssayStatus',
      label: assayStatus ? 'Pause Assay' : 'Resume Assay',
      icon: assayStatus ? ColumnsIcon : PlayIcon,
      onClick: () => onStatusToggle(),
    },
    {
      id: 'showDetails',
      label: 'Show Assay Details',
      icon: LinkExternalIcon,
      onClick: () => showDetailsFor(assayDetails),
    },
  ]

  const dropdownOptions = [
    ...(hideNormalDropdownOptions ? [] : normalModalOptions),
    {
      id: 'close',
      label: 'Go back',
      icon: XCircleIcon,
      onClick: () => onClose(),
    },
  ]

  const chartDatasets = useMemo(() => {
    const barData = mapAssayBarData(assayRunResult);
    return getBarChartComparisonDatasets(barData?.baseline, barData?.window);
  }, [assayRunResult]);

  const metric = assayRunResult?.summarizerMetadata?.metric;
  const score = assayRunResult?.score;

  const chartTitle = useMemo(() => hideCardTitle ? '' : `${assayName} (${metric} = ${assayScoreFormatter.format(score)})`, [assayName, hideCardTitle, metric, score])

  const containerRef = useRef();
  const dimensions = useRefDimensions(containerRef)

  const legendEntries = chartDatasets?.map(dataset => ({
    id: dataset.id,
    label: dataset.id,
    shape: 'square',
    color: dataset.barColor,
  }))

  const assayId = assayDetails?.id;
  const chartIDVal = `assay-${assayId}`;
  return (
    <ChartProvider
      chartID={`${chartIDVal}-run-details`}
      datasets={chartDatasets}
      dropdownOptions={dropdownOptions}
      prepareDataset={formatSequencedBarChartDatasets}
      usePaddedMax
    >
      <ChartWrapper
        chartId={`${chartIDVal}-chart`}
        containerRef={containerRef}
        chartTitle={chartTitle}
        isLoading={isLoading}
        onClose={onClose}
        message={message}
        showIconBasedMenu={showIconMenu}
        legendEntries={legendEntries}
        containerHeight={222}
        showLegend
      >
        <MultiBarChart
          chartId={`${chartIDVal}-run-details`}
          datasets={chartDatasets}
          margins={{
            top: 24,
            right: 32,
            bottom: 40,
            left: 48,
          }}
          labelRotation={0}
          yTickFormat={val => Number(val).toFixed(2)}
          numberOfYTicks={6}
          containerRef={containerRef}
          containerHeight={dimensions?.height && dimensions.height > 0 ? dimensions.height : 0}
          containerWidth={dimensions?.width && dimensions.width > 0 ? dimensions.width : 0}
          renderTooltip={renderResultsTooltip}
          stacked
        />
      </ChartWrapper>
    </ChartProvider>
  )
}

export const AssayWindowPointChart = ({
  onPointClick,
  assayId,
  assayName,
  assayDetails,
  assayRunResults = [],
  showDetailsFor,
  onStatusToggle,
  isLoading = false,
  showIconMenu = false,
  hideNormalDropdownOptions = false,
  hideCardTitle = false,
  message = null,
  loadingText = null,
}) => {
  const assayStatus = assayDetails?.active;
  
  const dropdownOptions = hideNormalDropdownOptions ? [] : [
    {
      id: 'changeAssayStatus',
      label: assayStatus ? 'Pause Assay' : 'Resume Assay',
      icon: assayStatus ? ColumnsIcon : PlayIcon,
      onClick: () => onStatusToggle(),
    },
    {
      id: 'showDetails',
      label: 'Show Assay Details',
      icon: LinkExternalIcon,
      onClick: () => showDetailsFor(assayDetails),
    },
  ]

  const chartTitle = useMemo(() => hideCardTitle ? '' : assayName, [assayName, hideCardTitle])

  const threshold = assayDetails?.alertThreshold ?? assayDetails?.alert_threshold;

  const assayPoints = useMemo(() => assayRunResults?.length ? mapAssayPointData(assayRunResults) : [], [assayRunResults])
  const assayDatasets = useMemo(() => deriveAssayChartDatasetsByStatus(assayName, assayPoints), [assayName, assayPoints])

  const containerRef = useRef();
  const dimensions = useRefDimensions(containerRef)

  const legendEntries = [
    {
      id: 'Ok',
      label: 'Ok',
      shape: 'circle',
      color: COLORS.success,
    },
    {
      id: 'Alert',
      label: 'Alert',
      shape: 'circle',
      color: COLORS.error,
    },
    {
      id: 'Threshold',
      label: 'Threshold',
      shape: 'line',
      color: COLORS.error,
    },
  ]

  const chartIDVal = `assay-${assayId}`;
  return (
    <ChartProvider
      chartID={`${chartIDVal}-history`}
      datasets={assayDatasets}
      dropdownOptions={dropdownOptions}
      usePaddedMax
    >
      <ChartWrapper
        chartId={`${chartIDVal}-history`}
        containerRef={containerRef}
        chartTitle={chartTitle}
        isLoading={isLoading}
        message={message}
        showIconBasedMenu={showIconMenu}
        loadingText={loadingText}
        legendEntries={legendEntries}
        containerHeight={240}
        cardStyle={{ height: '400px', minHeight: '400px' }}
        showLegend
      >
        <PointChart
          chartId={`${chartIDVal}-history`}
          datasets={assayDatasets}
          margins={{
            top: 24,
            right: 32,
            bottom: 40,
            left: 64,
          }}
          containerHeight={dimensions?.height && dimensions.height > 0 ? dimensions.height : 0}
          containerWidth={dimensions?.width && dimensions.width > 0 ? dimensions.width : 0}
          errorThreshold={threshold}
          onPointClick={onPointClick}
          numberOfXTicks={6}
          numberOfYTicks={5}
          labelRotation={0}
          xTickFormat={formatChartPointDate}
          yTickFormat={scientificNotationFormatter.format}
          renderTooltip={renderHistoryTooltip}
          showPoints
          useTickWrapping
        />
      </ChartWrapper>
    </ChartProvider>
  )
}


export const ModalAssayChart = ({
  assayRunResults,
  assayDetails,
  assayName,
  showDetailsFor,
  selectedRun,
  setSelectedRun,
  refetchAssays,
  isLoading = false,
  message = null,
  showIconMenu = false,
  hideNormalDropdownOptions = false,
  hideCardTitle = false,
  loadingText = null,
}) => {
  const assayId = assayDetails?.id;
  const assayStatus = assayDetails?.active;
  const { currentWorkspaceId } = useWorkspaceContext();

  const onPointClick = useCallback((selectedPoint) => {
    const selectedTime = selectedPoint.x.getTime();
    const selectedResult = assayRunResults.find((item) => {
      const itemTime = parseISO(item?.window_start || item?.windowSummary?.start || item?.window_summary?.start).getTime();
      return selectedTime === itemTime;
    });

    setSelectedRun(selectedResult);
  }, [assayRunResults, setSelectedRun])

  const onClose = useCallback(() => {
    setSelectedRun(null)
  }, [setSelectedRun]);

  const onStatusToggle = useCallback(() => {
    changeAssayStatus(assayId, currentWorkspaceId, !assayStatus);
    setTimeout(refetchAssays, 1000)
  }, [assayId, assayStatus, refetchAssays, currentWorkspaceId])

  if (selectedRun === null) {
    // Point Chart
    return (
      <AssayWindowPointChart
        assayId={assayId}
        assayName={assayName}
        assayRunResults={assayRunResults}
        assayDetails={assayDetails}
        assayStatus={assayStatus}
        message={message}
        isLoading={isLoading}
        showDetailsFor={showDetailsFor}
        onPointClick={onPointClick}
        onStatusToggle={onStatusToggle}
        showIconMenu={showIconMenu}
        hideNormalDropdownOptions={hideNormalDropdownOptions}
        hideCardTitle={hideCardTitle}
        loadingText={loadingText}
      />
    )
  }


  // Bar Chart with selected run data
  return (
    <AssayResultBarChart
      assayName={assayName}
      assayRunResult={selectedRun}
      assayDetails={assayDetails}
      assayStatus={assayStatus}
      message={message}
      isLoading={isLoading}
      showDetailsFor={showDetailsFor}
      onClose={onClose}
      onStatusToggle={onStatusToggle}
      showIconMenu={showIconMenu}
      hideNormalDropdownOptions={hideNormalDropdownOptions}
      hideCardTitle={hideCardTitle}
    />
  )
};

export default ModalAssayChart;
